/**
 * Shared consult-answer markdown renderer.
 * ==========================================
 *
 * Single source of truth for turning a Vet Consult answer (LLM markdown with
 * bracketed [n] citations) into sanitized HTML with linked citations.
 *
 * Previously this logic was copy-pasted across LmsAgentChat.vue and
 * VetClinicalChat.vue (with drift: one version sanitized, the other didn't)
 * and missing entirely from the public sample widget — which is why the
 * "Try a sample question" answer showed dead [1] markers. Every consult
 * surface should render answers through this one function so citations look
 * and behave identically whether the user is anonymous or logged in.
 *
 * Usage:
 *   import { renderConsultMarkdown } from '@/utils/consultMarkdown'
 *   <div v-html="renderConsultMarkdown(answer, sources)"></div>
 */

import { sanitizeHtml } from '@/utils/sanitize'

/**
 * Render a consult answer to sanitized HTML with linked [n] citations.
 *
 * @param {string} text - Raw LLM answer (markdown-ish with [n] citations)
 * @param {Array<{url?: string}>} sources - Ordered source list; index i maps
 *   to citation number i+1. Citations with a matching source URL become links.
 * @returns {string} Sanitized HTML safe for v-html
 */
export function renderConsultMarkdown(text, sources = []) {
  if (!text) return ''
  const urlMap = {}
  ;(sources || []).forEach((src, idx) => {
    if (src && src.url) urlMap[idx + 1] = src.url
  })
  const html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[(\d+)\]/g, (match, num) => {
      const url = urlMap[Number(num)]
      if (url) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="citation-link"><sup class="citation">[${num}]</sup></a>`
      }
      return `<sup class="citation">[${num}]</sup>`
    })
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^[•-] (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>')
    .replace(/^(?!<[hul])/, '<p>')
    .replace(/(?<![>])$/, '</p>')
  return sanitizeHtml(html)
}

export default { renderConsultMarkdown }
